import { useState } from 'react'
import { AppLayout, ErrorBanner, PageHeader } from '@/components/AppLayout'
import { useAuth } from '@/features/auth/useAuth'
import { useAsync } from '@/hooks/useAsync'
import { connectEventbrite, type EventbriteOrganizationChoice } from '@/lib/eventbriteImport'
import { disconnectEventbrite, getEventbriteStatus } from '@/lib/queries'

/**
 * Organization settings.
 *
 * Today this is the Eventbrite connection. The token is sent straight to the
 * Edge Function, validated against Eventbrite, and stored where no admin can
 * read it back — so this screen can show which account is connected, but never
 * the credential itself.
 */
export function SettingsPage() {
  const { memberships } = useAuth()
  const membership = memberships[0]
  const organization = membership?.organization
  const isOwner = membership?.role === 'owner'

  const status = useAsync(
    () => (organization ? getEventbriteStatus(organization.id) : Promise.resolve(null)),
    [organization?.id]
  )

  const [token, setToken] = useState('')
  const [eventbriteOrgId, setEventbriteOrgId] = useState('')
  const [choices, setChoices] = useState<EventbriteOrganizationChoice[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  async function handleConnect(eventbriteOrganizationId?: string) {
    if (!organization) return
    if (token.trim() === '') {
      setError('Enter your Eventbrite private token.')
      return
    }

    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const result = await connectEventbrite(
        organization.id,
        token.trim(),
        eventbriteOrganizationId ??
          (eventbriteOrgId.trim() === '' ? undefined : eventbriteOrgId.trim())
      )

      if (result.needsChoice) {
        // The token can act for more than one Eventbrite account, so ask rather
        // than pick one.
        setChoices(result.organizations)
        setNotice(null)
        return
      }

      setChoices(null)
      setToken('')
      setEventbriteOrgId('')
      setNotice(
        `Connected to ${result.eventbriteOrganizationName ?? result.eventbriteOrganizationId}.`
      )
      status.reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not connect that account')
    } finally {
      setBusy(false)
    }
  }

  async function handleDisconnect() {
    if (!organization) return
    const confirmed = window.confirm(
      'Disconnect Eventbrite? Importing events and attendees will stop working until you connect an account again. The token is removed from this app, but stays valid on Eventbrite until you revoke it there.'
    )
    if (!confirmed) return

    setBusy(true)
    setError(null)
    try {
      await disconnectEventbrite(organization.id)
      setNotice('Eventbrite disconnected.')
      status.reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not disconnect')
    } finally {
      setBusy(false)
    }
  }

  if (!organization) {
    return (
      <AppLayout>
        <div className="page">
          <PageHeader title="Settings" />
          <p className="field-hint">You are not a member of an organization yet.</p>
        </div>
      </AppLayout>
    )
  }

  const connected = status.data?.connected === true

  return (
    <AppLayout>
      <div className="page">
        <PageHeader title="Settings" breadcrumb={organization.name} />

        {error !== null && <ErrorBanner message={error} />}
        {notice !== null && <p className="banner banner-success">{notice}</p>}

        <div className="card">
          <div className="card-header">
            <h2>Eventbrite</h2>
            <span className={connected ? 'badge badge-success' : 'badge'}>
              {connected ? 'connected' : 'not connected'}
            </span>
          </div>

          {status.loading ? (
            <p className="field-hint">Loading…</p>
          ) : connected ? (
            <>
              <p className="field-hint">
                Importing events and attendees reads{' '}
                <strong>
                  {status.data?.eventbrite_organization_name ??
                    status.data?.eventbrite_organization_id}
                </strong>
                {status.data?.connected_at !== undefined &&
                  `, connected ${new Date(status.data.connected_at).toLocaleDateString()}`}
                .
              </p>
              <p className="field-hint">
                The token is stored where no one using this app can read it back, including you. To
                change it, connect again with a new token.
              </p>
            </>
          ) : (
            <p className="field-hint">
              Connect your Eventbrite account to import events and attendees. Without it you can
              still create events and add attendees by hand.
            </p>
          )}

          {!isOwner ? (
            <p className="banner">
              Only an organization owner can connect or disconnect an Eventbrite account.
            </p>
          ) : (
            <>
              <div className="field">
                <label className="field-label" htmlFor="eb-token">
                  {connected ? 'Replace with a new token' : 'Eventbrite private token'}
                </label>
                <p className="field-hint">
                  Eventbrite → Account Settings → Developer Links → API Keys. This token can read
                  your whole Eventbrite account, so it never reaches the browser again after you
                  save it.
                </p>
                <input
                  id="eb-token"
                  type="password"
                  value={token}
                  onChange={(e) => {
                    setToken(e.target.value)
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="Paste your private token"
                />
              </div>

              <div className="field">
                <label className="field-label" htmlFor="eb-org">
                  Eventbrite organization ID
                </label>
                <p className="field-hint">
                  Optional. Leave blank and we will detect it from the token, asking you only if the
                  token can act for more than one. Fill it in to pin a specific account — useful
                  when the token reaches an organization Eventbrite does not list back.
                </p>
                <input
                  id="eb-org"
                  type="text"
                  inputMode="numeric"
                  value={eventbriteOrgId}
                  onChange={(e) => {
                    setEventbriteOrgId(e.target.value)
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="e.g. 700000000001"
                />
              </div>

              {choices !== null && (
                <div className="field">
                  <p className="field-label">Which Eventbrite organization?</p>
                  <p className="field-hint">
                    That token can act for more than one account. Pick the one running these events.
                  </p>
                  <div className="row btn-group-stack">
                    {choices.map((choice) => (
                      <button
                        key={choice.id}
                        className="btn"
                        type="button"
                        disabled={busy}
                        onClick={() => void handleConnect(choice.id)}
                      >
                        {choice.name ?? choice.id}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="row btn-group-stack">
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={busy || token.trim() === ''}
                  onClick={() => void handleConnect()}
                >
                  {busy ? 'Checking…' : connected ? 'Replace token' : 'Connect Eventbrite'}
                </button>
                {connected && (
                  <button
                    className="btn btn-danger"
                    type="button"
                    disabled={busy}
                    onClick={() => void handleDisconnect()}
                  >
                    Disconnect
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
